// Thin fetch wrapper used by every route loader / mutation. The API issues a
// sealed `fd_session` cookie scoped to the parent domain
// (.trackwhatyoueat.com) on /auth/callback, so every request is credentialed
// (cookies travel automatically on same-site cross-origin XHR).
import type { BackgroundEstimateAccepted } from '@food-diary/shared';
import { addBreadcrumb } from './install-error-reporter';

export const API_BASE_URL =
  (import.meta.env.VITE_API_URL as string | undefined) ?? 'http://localhost:8787';

/**
 * `fetch()` rejected without a usable response, OR the response stream was
 * torn down mid-flight while we were consuming the body. The bare runtime
 * exception is `TypeError: network error`, which loses all routing context;
 * we wrap it so the client-error reporter captures `method` / `path` and,
 * for the mid-stream case, the `cf-ray` (`requestId`) that lets us correlate
 * to the original Worker request in observability.
 */
export class NetworkError extends Error {
  /** `cf-ray` of the upstream response, when one came back before the
   *  stream was torn down. Undefined for pre-response failures (DNS,
   *  CORS abort, tab suspended). */
  public readonly requestId: string | undefined;

  constructor(
    public readonly method: string,
    public readonly path: string,
    cause: unknown,
    options: { readonly requestId?: string } = {},
  ) {
    const causeMsg = cause instanceof Error ? cause.message : String(cause);
    super(`network error (${method} ${path}): ${causeMsg}`);
    this.name = 'NetworkError';
    this.requestId = options.requestId;
  }
}

export class ApiError extends Error {
  /** `cf-ray` of the upstream response, when set — lets the FE error
   *  reporter correlate to backend logs (Workers Observability /
   *  client_errors row). Undefined for fetch failures that never got a
   *  response. */
  public readonly requestId: string | undefined;
  /** HTTP method of the request that failed. */
  public readonly method: string | undefined;
  /** API path (without origin) of the request that failed. */
  public readonly path: string | undefined;

  constructor(
    public readonly status: number,
    public readonly body: string,
    options: {
      readonly message?: string;
      readonly requestId?: string;
      readonly method?: string;
      readonly path?: string;
    } = {},
  ) {
    super(options.message ?? `API ${String(status)}: ${body || 'error'}`);
    this.name = 'ApiError';
    this.requestId = options.requestId;
    this.method = options.method;
    this.path = options.path;
  }
}

function applyDefaultHeaders(headers: Headers, init: RequestInit, accept: string): void {
  headers.set('Accept', accept);
  // Don't pre-stamp Content-Type on multipart bodies — the browser
  // computes the boundary itself and our `multipart/form-data` header
  // would clobber that boundary, causing the API to reject the upload
  // with `expected multipart/form-data`.
  if (init.body != null && !headers.has('Content-Type') && !(init.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }
}

// Top-level navigation to the API's /auth/login route on a JSON 401 whose
// RFC 9457 Problem-JSON body carries `code: "unauthenticated"`. The API
// redirects to the WorkOS hosted AuthKit page; on success the SPA reloads
// with a fresh session cookie. `sessionStorage` guards against an infinite
// loop if a 401 keeps coming back for some non-session reason.
const REAUTH_GUARD_KEY = 'food-diary:auth-redirect-at';
const REAUTH_GUARD_WINDOW_MS = 30_000;

type ReauthOutcome = 'redirecting' | 'guarded' | 'not-applicable';

function isUnauthenticatedBody(body: string): boolean {
  if (!body) return false;
  try {
    const parsed = JSON.parse(body) as { readonly code?: unknown };
    return parsed.code === 'unauthenticated';
  } catch {
    return false;
  }
}

function maybeTriggerReauth(response: Response, body: string): ReauthOutcome {
  if (response.status !== 401) return 'not-applicable';
  if (!isUnauthenticatedBody(body)) return 'not-applicable';
  const storage = globalThis.sessionStorage as Storage | undefined;
  const location = globalThis.location as Location | undefined;
  if (!storage || !location) return 'guarded';
  const last = Number(storage.getItem(REAUTH_GUARD_KEY) ?? '0');
  if (Date.now() - last < REAUTH_GUARD_WINDOW_MS) return 'guarded';
  storage.setItem(REAUTH_GUARD_KEY, String(Date.now()));
  const returnTo = encodeURIComponent(location.href);
  location.href = `${API_BASE_URL}/auth/login?return_to=${returnTo}`;
  return 'redirecting';
}

// Block forever. We use this on the `redirecting` branch so callers don't see
// a transient ApiError flash in their UI before the page navigates away —
// React Query stays in `isPending`, components render their normal loading
// state, and the new page replaces them within a tick.
// Park forever. We use this on the redirect branch so callers stay in their
// loading state until the page navigates away — throwing here would briefly
// flash an ApiError in the UI before the new page replaces it. The executor
// returns the never-resolved sentinel symbol via the unused `_resolve` param
// just so the body has a statement.
const NEVER_RESOLVE = Symbol('never-resolve');

function neverResolve<T>(): Promise<T> {
  return new Promise<T>((_resolve) => {
    void _resolve;
    void NEVER_RESOLVE;
  });
}

async function fetchOrThrow(
  method: string,
  path: string,
  init: RequestInit,
  headers: Headers,
): Promise<Response> {
  try {
    return await fetch(`${API_BASE_URL}${path}`, {
      credentials: 'include',
      ...init,
      headers,
    });
  } catch (error) {
    throw new NetworkError(method, path, error);
  }
}

// Shared request preamble: apply default headers, fetch, run the 401 reauth
// guard, and throw a typed ApiError on any non-2xx. Returns the OK Response so
// callers can branch on status / content-type (JSON, NDJSON stream, …).
async function requestOk(path: string, init: RequestInit, accept: string): Promise<Response> {
  const headers = new Headers(init.headers);
  applyDefaultHeaders(headers, init, accept);
  const method = (init.method ?? 'GET').toUpperCase();
  if (method !== 'GET') addBreadcrumb('mutation', `${method} ${path}`);
  const response = await fetchOrThrow(method, path, init, headers);
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    if (response.status === 401) {
      const reauth = maybeTriggerReauth(response, text);
      if (reauth === 'redirecting') return neverResolve<Response>();
    }
    const ray = response.headers.get('cf-ray');
    throw new ApiError(response.status, text, {
      method,
      path,
      ...(ray ? { requestId: ray } : {}),
    });
  }
  return response;
}

export async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  applyDefaultHeaders(headers, init, 'application/json');
  const method = (init.method ?? 'GET').toUpperCase();
  if (method !== 'GET') addBreadcrumb('mutation', `${method} ${path}`);
  const response = await fetchOrThrow(method, path, init, headers);
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    if (response.status === 401) {
      const reauth = maybeTriggerReauth(response, text);
      if (reauth === 'redirecting') return neverResolve<T>();
    }
    const ray = response.headers.get('cf-ray');
    throw new ApiError(response.status, text, {
      method,
      path,
      ...(ray ? { requestId: ray } : {}),
    });
  }
  if (response.status === 204) {
    return undefined as T;
  }
  return (await response.json()) as T;
}

// NDJSON streaming consumer. Yields each JSON-line as it arrives so the
// caller can update UI incrementally (DESIGN §2.3 step 3 — per-item
// resolution badges appear as the resolver returns each food).
export async function* apiFetchNdjson<T>(path: string, init: RequestInit = {}): AsyncGenerator<T> {
  const method = (init.method ?? 'GET').toUpperCase();
  const response = await requestOk(path, init, 'application/x-ndjson');
  yield* readNdjsonBody<T>(response, method, path);
}

// Parse an NDJSON response body, yielding each JSON line as it arrives. Split
// out from `apiFetchNdjson` so `submitMeal` can stream a Response it has
// already inspected (to branch a 202 background ack vs a streamed commit).
async function* readNdjsonBody<T>(
  response: Response,
  method: string,
  path: string,
): AsyncGenerator<T> {
  if (!response.body) return;

  // Capture cf-ray before we start consuming the body: if the stream is
  // torn down mid-flight (a long-running NDJSON response where the
  // upstream Worker stalls long enough for an intermediate proxy or the
  // browser to give up), the resulting `TypeError: network error` from
  // `reader.read()` has no routing context. Re-throw as a typed
  // `NetworkError` so the client-error reporter persists method, path,
  // and the cf-ray of the original Worker request.
  const streamRequestId = response.headers.get('cf-ray') ?? undefined;
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  // Stream-decode + line-split: both `buffer` and the read loop are
  // intrinsically stateful. A non-mutating fold would require recursion
  // and lose the natural backpressure shape.
  /* eslint-disable functional/no-let */
  let buffer = '';
  const readNext = async (): Promise<ReadableStreamReadResult<Uint8Array>> => {
    try {
      return await reader.read();
    } catch (error) {
      throw new NetworkError(method, path, error, {
        ...(streamRequestId ? { requestId: streamRequestId } : {}),
      });
    }
  };
  let chunk = await readNext();
  while (!chunk.done) {
    buffer += decoder.decode(chunk.value, { stream: true });
    let nl = buffer.indexOf('\n');
    while (nl !== -1) {
      const line = buffer.slice(0, nl).trim();
      buffer = buffer.slice(nl + 1);
      if (line) yield JSON.parse(line) as T;
      nl = buffer.indexOf('\n');
    }
    chunk = await readNext();
  }
  buffer += decoder.decode();
  /* eslint-enable functional/no-let */
  const tail = buffer.trim();
  if (tail) yield JSON.parse(tail) as T;
}

export type MealSubmitOutcome<T> =
  | { readonly kind: 'background'; readonly accepted: BackgroundEstimateAccepted }
  | { readonly kind: 'stream'; readonly events: AsyncGenerator<T> };

// POST a meal submission and branch on the response (frictionless logging): an
// instant commit streams NDJSON per-item events, while a needs-AI submit
// returns a 202 JSON ack ("cooking, safe to close") that runs in the
// background. The caller streams the former and shows a reassurance for the
// latter.
export async function submitMeal<T>(
  path: string,
  init: RequestInit,
): Promise<MealSubmitOutcome<T>> {
  const method = (init.method ?? 'POST').toUpperCase();
  const response = await requestOk(path, init, 'application/x-ndjson');
  const contentType = response.headers.get('content-type') ?? '';
  if (response.status === 202 || !contentType.includes('ndjson')) {
    const accepted = (await response.json()) as BackgroundEstimateAccepted;
    return { kind: 'background', accepted };
  }
  return { kind: 'stream', events: readNdjsonBody<T>(response, method, path) };
}

export async function signOut(): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/auth/logout`, {
    method: 'POST',
    credentials: 'include',
  });
  // The api always clears its own cookie. If WorkOS handed back a logout URL
  // (so the SPA can also drop the WorkOS session cookie set on auth.* domain),
  // follow it; otherwise just reload the SPA shell.
  const body = (await response.json().catch(() => ({ logoutUrl: null }))) as {
    readonly logoutUrl: string | null;
  };
  globalThis.location.href = body.logoutUrl ?? `${globalThis.location.origin}/`;
}
